import React from "react";
import { Modal, Input, Button, Message, Tag, Divider } from "@arco-design/web-react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";

import { PostApi, SaveApi } from "../Api/AuthenticatedApi";

// One place to name the workspaces you run. Renaming reaches every org you
// belong to, not just the active one: with a site per org, switching into each
// just to retitle it is the wrong shape, and the Settings screen can only ever
// edit the org you're currently in.
export default function OrganizationsDialog({ visible, orgs, activeOrgId, onClose, onRenamed }) {
    const { getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();
    // Edits are held per org id until saved, so a half-typed name in one row
    // can't be mistaken for the stored one in another.
    const [names, setNames] = React.useState({});
    const [savingId, setSavingId] = React.useState(null);
    const [newName, setNewName] = React.useState("");
    const [creating, setCreating] = React.useState(false);

    // Reset to what the server says each time the dialog opens, so an abandoned
    // edit doesn't reappear later looking like it was saved.
    React.useEffect(() => {
        if (!visible) return;
        const next = {};
        (orgs || []).forEach((o) => { next[o.id] = o.name || ""; });
        setNames(next);
        setNewName("");
    }, [visible, orgs]);

    const rename = (org) => {
        const name = (names[org.id] || "").trim();
        if (!name || name === org.name || savingId) return;
        setSavingId(org.id);
        SaveApi(getAccessTokenSilently, `/api/orgs/${org.id}`,
            (updated) => {
                setSavingId(null);
                onRenamed(updated);
                Message.success("Organization renamed");
            },
            () => navigate("/login"),
            () => { setSavingId(null); Message.error("Failed to rename organization"); },
            { name });
    };

    const create = () => {
        const name = newName.trim();
        if (!name || creating) return;
        setCreating(true);
        PostApi(getAccessTokenSilently, "/api/orgs/",
            // A new org is created already-active, so a full reload lands on its
            // (empty) Home the same way switching organizations does.
            () => { window.location.href = "/"; },
            () => navigate("/login"),
            () => { setCreating(false); Message.error("Failed to create organization"); },
            { name });
    };

    return (
        <Modal
            visible={visible}
            title="Organizations"
            onCancel={onClose}
            footer={<Button onClick={onClose}>Done</Button>}
        >
            {(orgs || []).map((org) => {
                const edited = (names[org.id] || "").trim();
                const dirty = edited && edited !== org.name;
                return (
                    <div key={org.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <Input
                            value={names[org.id] || ""}
                            onChange={(v) => setNames((prev) => ({ ...prev, [org.id]: v }))}
                            onPressEnter={() => rename(org)}
                        />
                        {org.id === activeOrgId && <Tag size="small">Current</Tag>}
                        <Button
                            type="primary"
                            size="small"
                            disabled={!dirty}
                            loading={savingId === org.id}
                            onClick={() => rename(org)}
                        >
                            Save
                        </Button>
                    </div>
                );
            })}

            <Divider style={{ margin: "16px 0 12px" }} />

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Input
                    value={newName}
                    onChange={setNewName}
                    onPressEnter={create}
                    placeholder="New organization name"
                />
                <Button type="outline" size="small" disabled={!newName.trim()} loading={creating} onClick={create}>
                    Add
                </Button>
            </div>
            <div style={{ marginTop: 8, color: "var(--color-text-3)", fontSize: 12 }}>
                Each organization is a separate workspace with its own contacts, campaigns,
                files and sender addresses. Nothing is shared between them; you switch from
                this same menu. Adding one opens it straight away.
            </div>
        </Modal>
    );
}
